import React from "react"
import './_driverPicker.scss'
import MUIAutocomplete from "../muiAutocomplete/muiAutocomplete"
import { driverType } from "../../../shared/types"
import DriverListItem from "./DriverListItem/DriverListItem"
import { sortAlphabetically } from "../../../shared/utility"
import AddButton from "../button/addButton/AddButton"

interface DriverPickerProps {
  drivers: driverType[]                              // Available drivers to pick from
  selectedDrivers: driverType[]                      // Currently selected drivers
  value: driverType | null                           // Autocomplete current value
  setValue: React.Dispatch<React.SetStateAction<driverType | null>>  // Set autocomplete value
  loading?: boolean                                  // Loading state for autocomplete
  label: string                                      // Autocomplete label
  error?: boolean                                    // Error state
  disabled?: boolean                                 // Disable all interactions
  readOnly?: boolean                                 // View-only mode (no add/remove/edit)
  emptyMessage?: string                              // Message to show when no drivers selected
  onAdd?: (driver: driverType) => void               // Called when driver selected
  onRemove?: (driver: driverType) => void            // Called when driver removed
  onEdit?: (driver: driverType) => void              // Called when driver card clicked
  onNew?: () => void                                 // Called when "New Driver" clicked
  onChange?: () => void                              // Called on value change (for clearing errors)
  isAdmin?: boolean                                  // User is admin (can edit official drivers)
  parentIsOfficial?: boolean                         // Parent entity (team/series) is official
}

// Reusable driver picker component for forms.
const DriverPicker: React.FC<DriverPickerProps> = ({
  drivers,
  selectedDrivers,
  value,
  setValue,
  loading = false,
  label,
  error = false,
  disabled = false,
  readOnly = false,
  emptyMessage,
  onAdd,
  onRemove,
  onEdit,
  onNew,
  onChange,
  isAdmin = false,
  parentIsOfficial = false,
}) => {
  // Filter out already-selected drivers from the autocomplete options.
  const availableDrivers = drivers.filter(
    d => !selectedDrivers.some(sd => sd._id === d._id)
  )

  return (
    <div className="driver-picker">
      <MUIAutocomplete
        label={label}
        displayNew={readOnly ? "never" : "always"}
        customNewLabel="Driver"
        onNewMouseDown={readOnly ? undefined : () => onNew?.()}
        options={readOnly ? selectedDrivers : availableDrivers}
        value={value ? value.name : null}
        loading={loading}
        error={error}
        setObjValue={setValue}
        onLiClick={readOnly ? undefined : (val) => onAdd?.(val)}
        onChange={() => { if (onChange) { onChange() } }}
      />
      <div className="driver-picker-list">
        {selectedDrivers.length === 0 && emptyMessage ? (
          <p className="driver-picker-empty">{emptyMessage}</p>
        ) : (
          sortAlphabetically(selectedDrivers).map((driver: driverType, i: number) => {
            // Official drivers are read-only for non-admins (in addition to picker-level readOnly).
            const driverIsReadOnly = readOnly || (driver.official && !isAdmin)
            // Official drivers can only be removed from non-official parent entities.
            const canRemoveDriver = !disabled && !readOnly && !(parentIsOfficial && driver.official)
            return (
              <DriverListItem
                key={i}
                driver={driver}
                onRemove={driverIsReadOnly ? undefined : (d) => onRemove?.(d)}
                canRemove={canRemoveDriver}
                onClick={driverIsReadOnly ? undefined : () => onEdit?.(driver)}
                readOnly={driverIsReadOnly}
              />
            )
          })
        )}
      </div>
      {!readOnly && (
        <AddButton
          onClick={() => onNew?.()}
          absolute
        />
      )}
    </div>
  )
}

export default DriverPicker
